import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Globe } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEMS = [
  { key: "intl_travel", label: "Viagem", desc: "Custo único da viagem inicial do consultor" },
  { key: "intl_lawyers", label: "Advogados", desc: "Honorários legais (vistos, contratos)" },
  { key: "intl_visa", label: "Visto", desc: "Taxas de visto / autorização de residência" },
  { key: "intl_family_reunification", label: "Reagrupamento Familiar", desc: "Custos com agregado familiar" },
];

const AdminInternationalCosts = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/international-costs`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch {
      toast.error("Erro ao carregar custos internacionais");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/international-costs`, data, { headers: getAuthHeaders() });
      setData(r.data);
      toast.success("Custos internacionais guardados!");
    } catch {
      toast.error("Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">A carregar...</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-international-costs-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
            </Button>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              <span className="font-bold text-lg">Custos Internacionais</span>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[var(--brand-primary)] text-white" data-testid="save-intl-costs-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "A guardar..." : "Guardar"}
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <Card>
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground mb-4">
              Valores padrão pré-preenchidos no simulador quando o tipo de colocação é <span className="font-semibold">Internacional</span>.
              São custos pagos uma só vez (1×) e diluídos pelos meses faturáveis.
            </p>
            {ITEMS.map(({ key, label, desc }) => {
              const activeKey = `${key}_active`;
              const isActive = data[activeKey] !== false; // default true
              return (
                <div key={key} className="grid grid-cols-12 gap-3 items-end p-3 rounded-lg border bg-blue-50/30">
                  <div className="col-span-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setData({ ...data, [activeKey]: e.target.checked })}
                        className="w-4 h-4 accent-blue-600"
                        data-testid={`intl-${key}-active`}
                      />
                      <Label className="font-semibold text-blue-900">{label}</Label>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">{desc}</p>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Valor (€)</Label>
                    <Input
                      type="number"
                      value={data[key] ?? ""}
                      onChange={(e) => setData({ ...data, [key]: e.target.value === "" ? null : parseFloat(e.target.value) })}
                      className="font-mono"
                      placeholder="500"
                      disabled={!isActive}
                      data-testid={`intl-${key}-value`}
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Meses Faturação</Label>
                    <Select
                      value={(data[`${key}_months`] || 11).toString()}
                      onValueChange={(v) => setData({ ...data, [`${key}_months`]: parseInt(v) })}
                      disabled={!isActive}
                    >
                      <SelectTrigger data-testid={`intl-${key}-months`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="11">11 meses</SelectItem>
                        <SelectItem value="12">12 meses</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 text-xs text-right text-muted-foreground font-mono">
                    {isActive && data[key] && data[`${key}_months`]
                      ? `≈ ${(data[key] / data[`${key}_months`]).toFixed(0)}€/m`
                      : "—"}
                  </div>
                </div>
              );
            })}

            {/* Variáveis customizadas */}
            <div className="mt-6 p-4 rounded-lg border-2 border-dashed border-blue-300 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="font-semibold text-blue-900">Variáveis Adicionais</Label>
                  <p className="text-xs text-muted-foreground">Adicione custos extra específicos (ex.: cursos, alojamento, mudanças).</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const items = Array.isArray(data.custom_items) ? [...data.custom_items] : [];
                    items.push({ name: "", value: 0, months: 11, active: true });
                    setData({ ...data, custom_items: items });
                  }}
                  data-testid="intl-add-custom-btn"
                >
                  + Adicionar Variável
                </Button>
              </div>
              {(data.custom_items || []).length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">Sem variáveis adicionais. Clique no botão acima.</p>
              ) : (
                <div className="space-y-2">
                  {(data.custom_items || []).map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        type="checkbox"
                        checked={item.active !== false}
                        onChange={(e) => {
                          const items = [...(data.custom_items || [])];
                          items[idx] = { ...items[idx], active: e.target.checked };
                          setData({ ...data, custom_items: items });
                        }}
                        className="col-span-1 w-4 h-4 accent-blue-600 mt-5"
                        data-testid={`intl-custom-active-${idx}`}
                      />
                      <div className="col-span-4">
                        <Label className="text-xs">Nome</Label>
                        <Input
                          value={item.name || ""}
                          onChange={(e) => {
                            const items = [...(data.custom_items || [])];
                            items[idx] = { ...items[idx], name: e.target.value };
                            setData({ ...data, custom_items: items });
                          }}
                          placeholder="Ex.: Alojamento"
                          data-testid={`intl-custom-name-${idx}`}
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Valor (€)</Label>
                        <Input
                          type="number"
                          value={item.value ?? ""}
                          onChange={(e) => {
                            const items = [...(data.custom_items || [])];
                            items[idx] = { ...items[idx], value: parseFloat(e.target.value) || 0 };
                            setData({ ...data, custom_items: items });
                          }}
                          className="font-mono"
                          data-testid={`intl-custom-value-${idx}`}
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Meses Faturação</Label>
                        <Select
                          value={(item.months || 11).toString()}
                          onValueChange={(v) => {
                            const items = [...(data.custom_items || [])];
                            items[idx] = { ...items[idx], months: parseInt(v) };
                            setData({ ...data, custom_items: items });
                          }}
                        >
                          <SelectTrigger data-testid={`intl-custom-months-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="11">11 meses</SelectItem>
                            <SelectItem value="12">12 meses</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="col-span-1 mt-5 text-red-600 hover:bg-red-50"
                        onClick={() => {
                          const items = [...(data.custom_items || [])];
                          items.splice(idx, 1);
                          setData({ ...data, custom_items: items });
                        }}
                        data-testid={`intl-custom-remove-${idx}`}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminInternationalCosts;
